import { ModalStep } from "@/components/Evaluate/TableComponents/NewColumnModal/ModalRouter";
import { MCPConfiguration } from "@/types/evaluate";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "@heroicons/react/solid";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { XIcon } from "@heroicons/react/outline";
import { useState } from "react";

const HeaderPair = ({
  editable,
  name,
  setName,
  value,
  setValue,
  remove,
}: {
  editable: boolean;
  name: string;
  setName: (name: string) => void;
  value: string;
  setValue: (value: string) => void;
  remove: () => void;
}) => {
  return (
    <div className="relative grid grid-cols-3 gap-4 rounded-lg bg-gray-100 p-4 shadow-sm">
      <label
        htmlFor="authHeader"
        className="self-center text-sm font-medium text-gray-700"
      >
        Key:
      </label>
      <div className="col-span-2">
        <input
          id="authHeader"
          className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          value={name || ""}
          placeholder="X-API-Key"
          onChange={(e) => setName(e.target.value)}
          disabled={!editable}
        />
      </div>
      <label
        htmlFor="authToken"
        className="self-center text-sm font-medium text-gray-700"
      >
        Value:
      </label>
      <div className="col-span-2">
        <input
          id="authToken"
          className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          value={value || ""}
          placeholder="<APIKey>"
          onChange={(e) => setValue(e.target.value)}
          disabled={!editable}
        />
      </div>
      <BasicTooltip content="Remove header">
        <Button
          onClick={() => remove()}
          variant="destructiveLink"
          size="sm"
          className="absolute -right-1 -top-1 h-9 w-9 p-0"
          aria-label="Delete header"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </BasicTooltip>
    </div>
  );
};

const ServerConnectStep = ({
  navigateAway,
  configuration,
  setConfiguration,
  editable,
}: {
  navigateAway: () => void;
  configuration: Partial<MCPConfiguration>;
  setConfiguration: (
    data:
      | Partial<MCPConfiguration>
      | ((prevState: Partial<MCPConfiguration>) => Partial<MCPConfiguration>),
  ) => void;
  editable: boolean;
}) => {
  const [headers, setHeaders] = useState(
    configuration?.server_configuration?.headers
      ? Object.entries(configuration.server_configuration?.headers)
      : [],
  );

  const setServerUrl = (serverUrl: string) => {
    setConfiguration((config) => ({
      ...config,
      ...{
        server_configuration: {
          ...config.server_configuration,
          ...{
            server_url: serverUrl,
            headers: config.server_configuration?.headers || {},
          },
        },
      },
    }));
  };

  const updateHeaders = (
    func: (inp: [string, string][]) => [string, string][],
  ) => {
    setHeaders((prev) => {
      let newHeaders = func(prev);
      setConfiguration((config) => ({
        ...config,
        ...{
          server_configuration: {
            ...config.server_configuration,
            ...{
              server_url: config.server_configuration?.server_url || "",
              headers: Object.fromEntries(newHeaders),
            },
          },
        },
      }));
      return newHeaders;
    });
  };

  return (
    <ModalStep
      navigateNext={editable ? navigateAway : undefined}
      nextButtonText={editable ? "Save" : undefined}
      navigatePrevious={editable ? undefined : navigateAway}
    >
      <div className="grid grid-cols-[auto,1fr] gap-4">
        <h2 className="col-span-2 text-lg font-semibold">
          Configure MCP server.
        </h2>
        <label htmlFor="url" className="self-center text-left">
          Endpoint URL:
        </label>
        <div>
          <input
            id="url"
            className={`w-full rounded border border-gray-300 px-2 py-1 disabled:cursor-not-allowed disabled:bg-gray-50`}
            value={configuration?.server_configuration?.server_url || ""}
            placeholder="https://mpc.example.com/promptlayer"
            onChange={(e) => setServerUrl(e.target.value)}
            disabled={!editable}
          />
        </div>
        <label className="col-span-2 font-medium">Auth Headers</label>
        <div className="col-span-2 space-y-3">
          {headers.map((header, index) => (
            <HeaderPair
              key={index}
              editable={editable}
              name={header[0]}
              value={header[1]}
              setName={(newName) => {
                updateHeaders((prev) => {
                  let newVal = [...prev];
                  newVal[index][0] = newName;
                  return newVal;
                });
              }}
              setValue={(newValue) => {
                updateHeaders((prev) => {
                  let newVal = [...prev];
                  newVal[index][1] = newValue;
                  return newVal;
                });
              }}
              remove={() => {
                updateHeaders((prev) => {
                  let newVal = [...prev];
                  newVal.splice(index, 1);
                  return newVal;
                });
              }}
            />
          ))}
          <Button
            variant="outline"
            className="w-full"
            onClick={() =>
              updateHeaders((prev) => {
                let newHeader: [string, string] = ["", ""];
                return [...prev, ...[newHeader]];
              })
            }
            disabled={!editable}
          >
            <PlusIcon className="mr-2 h-5 w-5 text-gray-600" />
            Add Header
          </Button>
        </div>
      </div>
    </ModalStep>
  );
};

export default ServerConnectStep;
